import React from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

export default function AccessibilityStatement() {
  const { t, language } = useLanguage()

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    * { box-sizing:border-box; margin:0; padding:0; }
    :focus-visible { outline:2px solid #4D4E8D; outline-offset:3px; border-radius:6px; }
    .as-page { min-height:100vh; background:#E9EAEC; color:#151715; font-family:'Plus Jakarta Sans',-apple-system,sans-serif; -webkit-font-smoothing:antialiased; }

    .as-hero { text-align:center; padding:60px 24px 44px; max-width:820px; margin:0 auto; }
    .as-hero-badge { display:inline-flex; align-items:center; gap:8px; background:#EEF0F8; color:#4D4E8D; font-size:12px; font-weight:700; letter-spacing:.04em; padding:7px 16px; border-radius:20px; margin-bottom:20px; }
    .as-hero h1 { font-size:clamp(30px,3.6vw,42px); font-weight:800; color:#151715; line-height:1.14; letter-spacing:-.025em; margin-bottom:14px; }
    .as-hero-sub { font-size:16px; font-weight:500; color:#5C6065; max-width:600px; margin:0 auto 12px; line-height:1.7; }
    .as-updated { font-size:13px; color:#8A8F94; font-weight:500; }

    .as-content { max-width:820px; margin:0 auto; padding:8px 24px 64px; }

    .as-compliance { display:flex; gap:18px; align-items:flex-start; background:#fff; border:1px solid #E2E4E7; border-left:4px solid #4D4E8D; border-radius:16px; padding:24px 26px; margin-bottom:28px; box-shadow:0 8px 22px rgba(21,23,21,0.05); }
    .as-compliance-icon { font-size:30px; flex-shrink:0; }
    .as-compliance-title { font-size:16px; font-weight:700; color:#151715; margin-bottom:6px; }
    .as-compliance-desc { font-size:14px; font-weight:500; color:#5C6065; line-height:1.7; }

    .as-section { margin-bottom:30px; }
    .as-section h2 { font-size:20px; font-weight:800; color:#151715; margin-bottom:12px; letter-spacing:-.015em; }
    .as-section p { font-size:14.5px; font-weight:500; color:#5C6065; line-height:1.8; margin-bottom:12px; }

    .as-feature-list { list-style:none; display:grid; grid-template-columns:1fr 1fr; gap:10px 20px; margin-top:8px; }
    @media(max-width:640px){ .as-feature-list { grid-template-columns:1fr; } }
    .as-feature-item { display:flex; align-items:flex-start; gap:10px; font-size:14px; font-weight:500; color:#3A3D40; line-height:1.5; }
    .as-feature-check { display:inline-flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:50%; background:#EAF2EE; color:#5E8A72; font-size:12px; font-weight:800; flex-shrink:0; margin-top:1px; }

    .as-issues-list { list-style:none; margin:10px 0; }
    .as-issues-item { display:flex; align-items:flex-start; gap:12px; font-size:14px; font-weight:500; color:#3A3D40; line-height:1.6; margin-bottom:8px; }
    .as-issues-dot { width:7px; height:7px; border-radius:50%; background:#4D4E8D; flex-shrink:0; margin-top:7px; }

    .as-contact { background:#fff; border:1px solid #E2E4E7; border-radius:18px; padding:28px 26px; margin-bottom:28px; box-shadow:0 8px 22px rgba(21,23,21,0.05); }
    .as-contact h2 { font-size:19px; font-weight:800; color:#151715; margin-bottom:10px; letter-spacing:-.015em; }
    .as-contact p { font-size:14.5px; font-weight:500; color:#5C6065; line-height:1.75; margin-bottom:18px; }
    .as-contact-methods { display:flex; gap:12px; flex-wrap:wrap; }
    .as-contact-btn { display:inline-flex; align-items:center; gap:8px; font-size:14px; font-weight:700; padding:12px 22px; border-radius:11px; text-decoration:none; transition:all .2s; }
    .as-contact-btn-gold { background:#4D4E8D; color:#fff; box-shadow:0 8px 18px rgba(77,78,141,0.22); }
    .as-contact-btn-gold:hover { background:#41427a; transform:translateY(-2px); }
    .as-contact-btn-outline { background:#fff; color:#4D4E8D; border:1.5px solid #CFD3D8; }
    .as-contact-btn-outline:hover { border-color:#4D4E8D; transform:translateY(-2px); }
  `

  const isEs = language === 'es'

  return (
    <div className="as-page">
      <style>{styles}</style>

      {/* ADA: Skip link */}
      
      <SiteHeader />

      {/* HERO */}
      <header className="as-hero">
        <div className="as-hero-badge" aria-label="WCAG 2.1 Level AA Compliant">
          <span aria-hidden="true">♿</span>
          WCAG 2.1 Level AA
        </div>
        <h1>
          {isEs ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
        </h1>
        <p className="as-hero-sub">
          {isEs
            ? 'Notary Signing Vault, LLC está comprometido con garantizar la accesibilidad digital para todas las personas, incluidas aquellas con discapacidades.'
            : 'Notary Signing Vault, LLC is committed to ensuring digital accessibility for all people, including those with disabilities.'}
        </p>
        <p className="as-updated">
          {isEs ? 'Última actualización: abril de 2026' : 'Last updated: April 2026'}
        </p>
      </header>

      {/* MAIN CONTENT */}
      <main id="nsv-main" tabIndex={-1}>
        <div className="as-content">

          {/* Compliance badge */}
          <div className="as-compliance" role="region" aria-label="Compliance status">
            <div className="as-compliance-icon" aria-hidden="true">♿</div>
            <div className="as-compliance-text">
              <div className="as-compliance-title">
                {isEs ? 'Objetivo de Conformidad' : 'Conformance Target'}
              </div>
              <div className="as-compliance-desc">
                {isEs
                  ? 'Nos esforzamos por cumplir con las Pautas de Accesibilidad al Contenido Web (WCAG) 2.1, Nivel AA. Estas pautas explican cómo hacer que el contenido web sea más accesible para personas con discapacidades.'
                  : 'We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1, Level AA. These guidelines explain how to make web content more accessible to people with disabilities.'}
              </div>
            </div>
          </div>

          {/* Our commitment */}
          <section className="as-section" aria-labelledby="commitment-heading">
            <h2 id="commitment-heading">
              {isEs ? 'Nuestro Compromiso' : 'Our Commitment'}
            </h2>
            <p>
              {isEs
                ? 'Notary Signing Vault, LLC cree que todos merecen acceso igual a los servicios notariales y de firma de préstamos, independientemente de sus capacidades. Nos comprometemos continuamente a mejorar la experiencia de usuario para todos nuestros clientes.'
                : 'Notary Signing Vault, LLC believes everyone deserves equal access to notary and loan signing services, regardless of their abilities. We are continuously working to improve the user experience for all of our clients.'}
            </p>
            <p>
              {isEs
                ? 'Hemos tomado las siguientes medidas para garantizar la accesibilidad en toda nuestra plataforma:'
                : 'We have taken the following steps to ensure accessibility throughout our platform:'}
            </p>
          </section>

          {/* Accessibility features */}
          <section className="as-section" aria-labelledby="features-heading">
            <h2 id="features-heading">
              {isEs ? 'Características de Accesibilidad' : 'Accessibility Features'}
            </h2>
            <ul className="as-feature-list">
              {[
                isEs ? 'Navegación completa por teclado' : 'Full keyboard navigation support',
                isEs ? 'Compatibilidad con lectores de pantalla' : 'Screen reader compatibility',
                isEs ? 'Indicadores de enfoque visibles' : 'Visible focus indicators',
                isEs ? 'Texto alternativo en todas las imágenes' : 'Alternative text on all images',
                isEs ? 'Contraste de color WCAG AA' : 'WCAG AA color contrast ratios',
                isEs ? 'Estructura de encabezados semánticos' : 'Semantic heading structure',
                isEs ? 'Etiquetas ARIA en formularios' : 'ARIA labels on all form fields',
                isEs ? 'Enlace "Saltar al contenido"' : 'Skip to main content links',
                isEs ? 'Anuncios de errores accesibles' : 'Accessible error announcements',
                isEs ? 'Soporte bilingüe (inglés y español)' : 'Bilingual support (English & Spanish)',
                isEs ? 'Diseño responsivo para móviles' : 'Fully mobile-responsive design',
                isEs ? 'Sin contenido que parpadee o destelle' : 'No flashing or strobing content',
              ].map(feature => (
                <li key={feature} className="as-feature-item">
                  <span className="as-feature-check" aria-hidden="true">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </section>

          {/* Technical specifications */}
          <section className="as-section" aria-labelledby="tech-heading">
            <h2 id="tech-heading">
              {isEs ? 'Especificaciones Técnicas' : 'Technical Specifications'}
            </h2>
            <p>
              {isEs
                ? 'La accesibilidad de notarysigningvault.com se basa en las siguientes tecnologías:'
                : 'The accessibility of notarysigningvault.com relies on the following technologies:'}
            </p>
            <ul className="as-issues-list" aria-label={isEs ? 'Tecnologías utilizadas' : 'Technologies used'}>
              {['HTML5', 'CSS3', 'WAI-ARIA 1.2', 'React 18', isEs ? 'JavaScript moderno' : 'Modern JavaScript'].map(tech => (
                <li key={tech} className="as-issues-item">
                  <div className="as-issues-dot" aria-hidden="true" />
                  {tech}
                </li>
              ))}
            </ul>
            <p>
              {isEs
                ? 'Nuestra plataforma ha sido probada con los siguientes navegadores y tecnologías de asistencia: Chrome con NVDA, Safari con VoiceOver (macOS e iOS), y Firefox con JAWS.'
                : 'Our platform has been tested with the following browsers and assistive technologies: Chrome with NVDA, Safari with VoiceOver (macOS and iOS), and Firefox with JAWS.'}
            </p>
          </section>

          {/* Known limitations */}
          <section className="as-section" aria-labelledby="limitations-heading">
            <h2 id="limitations-heading">
              {isEs ? 'Limitaciones Conocidas' : 'Known Limitations'}
            </h2>
            <p>
              {isEs
                ? 'Aunque nos esforzamos por alcanzar el máximo nivel de accesibilidad, puede haber algunas áreas que aún estamos mejorando:'
                : 'Although we strive for the highest level of accessibility, there may be some areas we are still improving:'}
            </p>
            <ul className="as-issues-list" aria-label={isEs ? 'Limitaciones conocidas' : 'Known limitations'}>
              {[
                isEs
                  ? 'Algunos documentos PDF generados por terceros pueden no ser completamente accesibles'
                  : 'Some third-party generated PDF documents may not be fully accessible',
                isEs
                  ? 'El portal de Stripe para pagos es administrado por un tercero y puede tener sus propias limitaciones de accesibilidad'
                  : 'The Stripe payment portal is managed by a third party and may have its own accessibility limitations',
                isEs
                  ? 'Ciertos gráficos de datos complejos pueden requerir mejoras adicionales de descripción'
                  : 'Certain complex data charts may require additional description improvements',
              ].map(issue => (
                <li key={issue} className="as-issues-item">
                  <div className="as-issues-dot" aria-hidden="true" />
                  {issue}
                </li>
              ))}
            </ul>
            <p>
              {isEs
                ? 'Estamos trabajando activamente para resolver estas limitaciones. Si encuentra un problema, por favor contáctenos.'
                : 'We are actively working to resolve these limitations. If you encounter an issue, please contact us.'}
            </p>
          </section>

          {/* Feedback & contact */}
          <div className="as-contact" role="region" aria-labelledby="contact-heading">
            <h2 id="contact-heading">
              {isEs ? '¿Necesita Ayuda o Encontró un Problema?' : 'Need Help or Found an Issue?'}
            </h2>
            <p>
              {isEs
                ? 'Si experimenta barreras de accesibilidad en nuestro sitio web o necesita información en un formato alternativo, nos encantaría saber de usted. Intentamos responder a todos los comentarios de accesibilidad dentro de 2 días hábiles.'
                : 'If you experience any accessibility barriers on our website, or need information in an alternative format, we would love to hear from you. We aim to respond to all accessibility feedback within 2 business days.'}
            </p>
            <div className="as-contact-methods">
              <a
                href="mailto:nancy@notarysigningvault.com?subject=Accessibility%20Feedback"
                className="as-contact-btn as-contact-btn-gold"
                aria-label={isEs
                  ? 'Enviar comentarios de accesibilidad por correo electrónico'
                  : 'Send accessibility feedback by email'}
              >
                <span aria-hidden="true">✉️</span>
                {isEs ? 'Enviar Comentarios' : 'Send Feedback'}
              </a>
              <Link
                to="/login"
                className="as-contact-btn as-contact-btn-outline"
                aria-label={isEs ? 'Iniciar sesión en su cuenta' : 'Sign in to your account'}
              >
                <span aria-hidden="true">🔐</span>
                {isEs ? 'Iniciar Sesión' : 'Sign In'}
              </Link>
            </div>
          </div>

          {/* Legal */}
          <section className="as-section" aria-labelledby="legal-heading">
            <h2 id="legal-heading">
              {isEs ? 'Marco Legal' : 'Legal Framework'}
            </h2>
            <p>
              {isEs
                ? 'Esta declaración de accesibilidad se preparó de conformidad con la Ley de Estadounidenses con Discapacidades (ADA), Título III, y las Pautas de Accesibilidad al Contenido Web (WCAG) 2.1. Notary Signing Vault, LLC está comprometido a proporcionar un acceso igual y oportunidades iguales a las personas con diversas discapacidades.'
                : 'This accessibility statement was prepared in accordance with the Americans with Disabilities Act (ADA), Title III, and the Web Content Accessibility Guidelines (WCAG) 2.1. Notary Signing Vault, LLC is committed to providing equal access and equal opportunity to people with diverse abilities.'}
            </p>
            <p>
              {isEs
                ? 'Si cree que no hemos respondido adecuadamente a su solicitud de accesibilidad, tiene derecho a presentar una queja ante el Departamento de Justicia de los Estados Unidos.'
                : 'If you believe we have not responded adequately to your accessibility request, you have the right to file a complaint with the U.S. Department of Justice.'}
            </p>
          </section>

        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
